import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Pagination,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery,
  Tooltip
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getProfileAction,
  deleteUserAction,
  clearProfileData,
  getSearchUserList
} from "../../../redux/actions/agencyActions";
import UserProfileForm from "./UserProfileForm";
import Loader from "../../loader/loader";
import { getSelectedUserDetailsAction, getAllLocationsAction } from "../../../redux/actions/agencyActions";

const UserList = () => {
  const firstLoad = React.useRef(true);
   const locationLoad = React.useRef(true);
  const [status, setStatus] = useState("All");
  const [defaultLocation, setDefaultLocation] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filtering, setFiltering] = useState(false);
  const [searchError, setSearchError] = useState("");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const dispatch = useDispatch();
  const get_all_location = useSelector((state) =>
    Array.isArray(state?.agency?.get_all_locations_list)
      ? state.agency.get_all_locations_list
      : []
  );

  const allProfileData = useSelector((state) => state.agency.allProfile);
  const searchProfileData = useSelector((state) => state.agency.allProfile);

  const currentProfileData = isSearching ? searchProfileData : allProfileData;
  const userDetails = currentProfileData?.content || [];
  const userDetails_pagination = currentProfileData || {};

  const userPagination = currentProfileData || {};
  const locationDetails = useSelector((state) => state?.location?.allLocation);

  let locationList = [];
  if (Array.isArray(locationDetails)) {
    locationList = locationDetails.map((location, index) => ({
      value: location.locationDescription,
      label: location.locationDescription,
      id: `${location.locationDescription}-${index}`,
    }));
  }

  const [searchQuery, setSearchQuery] = useState("");

useEffect(() => {
  if (locationLoad.current) {
    locationLoad.current = false;
    dispatch(getAllLocationsAction());
  }
}, [dispatch]);

useEffect(() => {
  if (firstLoad.current && !isSearching) {
    firstLoad.current = false;
    dispatch(getProfileAction(page, rowsPerPage));
  }
}, [page, rowsPerPage, isSearching, dispatch]);

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isSearching) {
      const payload = {
        search: searchQuery.trim(),
        ...(status !== "All" && { status }),
        ...(Array.isArray(defaultLocation) && defaultLocation.length > 0 && {
          location: defaultLocation
        })
      };
      dispatch(getSearchUserList(payload, newPage, rowsPerPage));
    } else {
      dispatch(getProfileAction(newPage, rowsPerPage));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (isSearching) {
      const payload = {
        search: searchQuery.trim(),
        ...(status !== "All" && { status }),
        ...(Array.isArray(defaultLocation) && defaultLocation.length > 0 && {
          location: defaultLocation
        })
      };
      dispatch(getSearchUserList(payload, 0, newRowsPerPage));
    } else {
      dispatch(getProfileAction(0, newRowsPerPage));
    }
  };

  const callbackfn = (user) => {
    setSelectedUser(user);
  }

  const handleEditClick = (user) => {
    dispatch(getSelectedUserDetailsAction(user.id, callbackfn));
    navigate("/user-profile", { state: { user } });
  };

  const handleDeleteUser = (id) => {
    setSelectedUserId(id);
    setOpenDialog(true);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirmDelete = () => {
    setOpenDialog(false);

    if (selectedUserId !== null) {
      dispatch(deleteUserAction(selectedUserId))
        .then(() => {
          if (isSearching) {
            handleSearch();
          } else {
            dispatch(getProfileAction(page, rowsPerPage));
          }
          setSnackbar({
            open: true,
            message: 'User deleted successfully',
            severity: 'success',
          });
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: 'Failed to delete user',
            severity: 'error',
          });
        })
        .finally(() => {
          setSelectedUserId(null);
        });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUserId(null);
  };

  const handleSearch = () => {
    if (searchQuery.trim().length > 50) {
      setSnackbar({
        open: true,
        message: "Search keyword cannot exceed 50 characters.",
        severity: "error",
      });
      return;
    }

    const payload = {
      search: searchQuery.trim(),
      ...(status !== "All" && { status }),
      ...(Array.isArray(defaultLocation) && defaultLocation.length > 0 && {
        location: defaultLocation
      })
    };

    setFiltering(true);
    setPage(0);

    dispatch(getSearchUserList(payload, 0, rowsPerPage))
      .then(() => {
        setIsSearching(true);
      })
      .catch((err) => {
        if (err.response?.status === 400) {
          setSnackbar({
            open: true,
            message: "Invalid input. Please check search or filter values.",
            severity: "error",
          });
        }
      })
      .finally(() => {
        setFiltering(false);
      });
  };

  const handleResetFilters = () => {
    setStatus("All");
    setDefaultLocation("All");
    setSearchQuery("");
    setPage(0);
    setIsSearching(false);
    dispatch(clearProfileData());
    dispatch(getProfileAction(0, rowsPerPage));
  };

  const renderFieldValue = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'object') {
      return value.displayName || value.name || value.description || JSON.stringify(value);
    }

    return String(value);
  };

  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    const textStr = String(text);
    if (textStr.length <= maxLength) return textStr;
    return textStr.substring(0, maxLength) + "...";
  };

  return (
    <Box sx={{
      padding: isMobile ? 2 : 3,
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>

        {isEditing ? (
          <UserProfileForm user={selectedUser} />
        ) : (
          <>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              marginBottom: isMobile ? 2 : 3,
              gap: isMobile ? 2 : 0
            }}>
              <Typography
                id="allusers"
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: isMobile ? '18px' : '24px',
                  fontWeight: 'bold'
                }}
              >
                {isSearching ? 'Search Results' : 'All Users'} ({userPagination.totalElements || 0})
              </Typography>
            </Box>

            <Box sx={{
              display: "flex",
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isTablet ? 'wrap' : 'nowrap',
              gap: isMobile ? 2 : 1,
              marginBottom: 3,
              alignItems: isMobile ? 'stretch' : 'flex-end'
            }}>
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: isMobile ? '100%' : '200px',
                flex: isMobile ? 1 : 'none'
              }}>
                <Typography
                  sx={{
                    fontSize: isMobile ? "14px" : "16px",
                    fontFamily: '"Poppins", sans-serif',
                    marginBottom: 1
                  }}
                >
                  Active Status
                </Typography>
                <Select
                  id="selectactivestatus"
                  name="status"
                  value={status}
                  onChange={handleStatusChange}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="DEACTIVATED">Not Active</MenuItem>
                   <MenuItem value="SUSPENDED">Suspended</MenuItem>
                </Select>
              </Box>

              <Box sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: isMobile ? '100%' : '250px',
                flex: isMobile ? 1 : 'none'
              }}>
                <Typography sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: isMobile ? "14px" : "16px",
                  marginBottom: 1
                }}>
                  Search Keyword
                </Typography>
                <TextField
                  id="search-keyword"
                  variant="outlined"
                  placeholder="Search by name, email, etc."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 50) {
                      setSearchQuery(value);
                      setSearchError("");
                    } else {
                      setSearchError("Maximum 50 characters allowed");
                    }
                  }}
                  inputProps={{ maxLength: 50 }}
                  error={!!searchError}
                  helperText={searchError}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                />
              </Box>

              <Box sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: isMobile ? '100%' : '250px',
                flex: isMobile ? 1 : 'none'
              }}>
                <Typography sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: isMobile ? "14px" : "16px",
                  marginBottom: 1
                }}>
                  User in Default Location
                </Typography>
                <Autocomplete
                  multiple
                  id="autocomplete-default-location"
                  options={get_all_location}
                  getOptionLabel={(option) => option.locationDescription}
                  value={get_all_location?.filter((loc) =>
                    defaultLocation.includes(loc.locationId)
                  )}
                  onChange={(event, newValue) => {
                    const selectedIds = newValue.map((item) => item.locationId);
                    setDefaultLocation(selectedIds);
                    setPage(0);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Locations"
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      sx={{ fontFamily: '"Poppins", sans-serif' }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.locationId}>
                      {option.locationDescription}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
                  disableCloseOnSelect
                  fullWidth
                />
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1,
                minWidth: isMobile ? '100%' : 'auto'
              }}>
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  size={isMobile ? "small" : "large"}
                  sx={{
                    minWidth: isMobile ? '100%' : 'auto',
                    fontSize: isMobile ? '12px' : '14px',
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  size={isMobile ? "small" : "large"}
                  sx={{
                    minWidth: isMobile ? '100%' : 'auto',
                    fontSize: isMobile ? '12px' : '14px',
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  id="buttonadduser"
                  name="addUser"
                  className="btn-primary"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: isMobile ? '100%' : 'auto',
                    fontSize: isMobile ? '12px' : '14px', '&:focus': {
                    outline: '2px solid #E69C00',
                },textTransform: 'none', fontSize: '15px',
                  }}
                  onClick={() => {
                    dispatch(clearProfileData());
                    navigate("/user-profile");
                  }}
                >
                  Add New User
                </Button>
              </Box>
            </Box>
            <Paper
              sx={{
                width: "100%",
                overflow: "hidden",
                borderRadius: "10px",
                boxShadow: isMobile ? 1 : 2
              }}
            >
              <TableContainer sx={{
                maxHeight: isMobile ? '60vh' : 'none',
                minHeight: '200px'
              }}>
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  sx={{
                    minWidth: isMobile ? '800px' : '650px',
                    '& .MuiTableCell-root': {
                      fontSize: isMobile ? '11px' : '14px',
                      padding: isMobile ? '6px 8px' : '12px 16px',
                      whiteSpace: isMobile ? 'nowrap' : 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: isMobile ? '120px' : '200px'
                    }
                  }}
                  size={isMobile ? "small" : "medium"}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '80px' : '120px'
                        }}
                      >
                        First Name
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '80px' : '120px'
                        }}
                      >
                        Last Name
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '90px' : '120px'
                        }}
                      >
                        User Name
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '120px' : '150px'
                        }}
                      >
                        Email Address
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '70px' : '100px'
                        }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '70px' : '100px'
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '100px' : '150px'
                        }}
                      >
                        Default Location
                      </TableCell>
                      <TableCell
                        className="styled-table-heading"
                        align="right"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '80px' : '100px'
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtering ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                            <Loader />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : userDetails?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="subtitle1" color="textSecondary">
                            {isSearching ? "No search results found" : "No Data Available"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      userDetails?.map((user, index) => (
                        <TableRow className="styled-table-row" key={user.id || index}>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.firstName)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.firstName))}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.firstName)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.lastName)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.lastName))}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.lastName)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.userName)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.userName))}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.userName)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.email)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.email), 20)}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.email)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.newRole || user.role || user.userRole)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.newRole || user.role || user.userRole))}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.newRole || user.role || user.userRole)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.status)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.status))}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.status)
                            )}
                          </TableCell>
                          <TableCell>
                            {isMobile ? (
                              <Tooltip title={renderFieldValue(user.defaultLocation)} arrow placement="top">
                                <span>{getTruncatedText(renderFieldValue(user.defaultLocation), 20)}</span>
                              </Tooltip>
                            ) : (
                              renderFieldValue(user.defaultLocation)
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={() => handleEditClick(user)}
                                id={`editbutton${user.id}`}
                                sx={{ p: isMobile ? '4px' : '8px' }}
                              >
                                <EditIcon fontSize={isMobile ? "small" : "medium"} />
                              </IconButton>
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={() => handleDeleteUser(user.id)}
                                sx={{ p: isMobile ? '4px' : '8px' }}
                              >
                                <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Section */}
              {userDetails_pagination.totalElements > 0 && (
                <Box sx={{
                  width: "100%",
                  overflow: "hidden",
                  padding: isMobile ? "12px 0" : "16px 0"
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="flex-end"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={2}
                    sx={{
                      gap: isMobile ? 1 : 2
                    }}
                  >
                    {/* Rows per page dropdown */}
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{
                        fontSize: isMobile ? "11px" : "14px",
                        marginRight: "8px"
                      }}>
                        Rows per page:
                      </Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={handleChangeRowsPerPage}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          height: isMobile ? '32px' : '36px',
                          fontSize: isMobile ? '11px' : '14px',
                          minWidth: isMobile ? '60px' : '80px',
                          '& .MuiSelect-select': {
                            padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                          }
                        }}
                      >
                        {(userDetails_pagination.totalPages === 0 ? [] : [5, 10, 15, 25, 50, 100]).map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Pagination controls */}
                    <Pagination
                      count={Math.ceil(userDetails_pagination.totalElements / rowsPerPage)}
                      page={page + 1}
                      onChange={(event, value) => handleChangePage(event, value - 1)}
                      showFirstButton
                      showLastButton
                      size={isMobile ? "small" : "medium"}
                      siblingCount={isMobile ? 0 : 1}
                      boundaryCount={1}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: isMobile ? '12px' : '14px',
                          minWidth: isMobile ? '28px' : '32px',
                          height: isMobile ? '28px' : '32px'
                        }
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            handleCloseDialog(event, reason);
          }}
          aria-labelledby="delete-confirmation-dialog-title"
          aria-describedby="delete-confirmation-dialog-description"
          maxWidth="sm"
          fullWidth={isMobile}
          PaperProps={{
            sx: {
              margin: isMobile ? 1 : 3,
              width: isMobile ? 'calc(100% - 16px)' : 'auto'
            }
          }}
        >
          <DialogTitle
            className="btn-primary"
            sx={{
              height: isMobile ? '56px' : '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 12px' : '0 16px',
              fontSize: isMobile ? '16px' : '18px'
            }}
            id="delete-confirmation-dialog-title"
          >
            {"Delete Confirmation"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={{
                marginTop: '15px',
                fontSize: isMobile ? '14px' : '16px'
              }}
              id="delete-confirmation-dialog-description"
            >
              Are you sure you want to delete this user? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{
            padding: isMobile ? '12px 16px' : '16px 24px',
            gap: 1
          }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="outlined"
              color="error"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isMobile ? 'center' : 'right'
          }}
          sx={{
            top: isMobile ? '60px' : '24px',
            '& .MuiSnackbarContent-root': {
              fontSize: isMobile ? '12px' : '14px'
            }
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              whiteSpace: 'pre-line',
              fontSize: isMobile ? '12px' : '14px',
              width: isMobile ? '90vw' : 'auto',
              maxWidth: isMobile ? '350px' : '500px'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
}

export default UserList;